import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField, useAssignees, useLookups } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const F = ({ label, k, f, set, req, ...p }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}{req && <span className="text-brand-700"> *</span>}</Label>
    <Input value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={`company-field-${k}`} {...p} /></div>
);
const Section = ({ title, children }) => <div className="space-y-3"><p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{title}</p>{children}</div>;
const EXTRA = [["trade_name", "Nome commerciale"], ["sdi_code", "Codice SDI"], ["pec", "PEC"], ["website", "Sito web"], ["phone", "Telefono"],
  ["email", "Email"], ["address", "Indirizzo"], ["zip_code", "CAP"], ["city", "Città"], ["province", "Provincia"], ["region", "Regione"], ["country", "Paese"]];

export const CompanyFormDialog = ({ open, onOpenChange, company, onSaved }) => {
  const { me, can } = useAuth();
  const lk = useLookups();
  const users = useAssignees();
  const edit = !!company;
  const [f, setF] = useState({});
  const [contact, setContact] = useState({ first_name: "", last_name: "", job_title: "", email: "" });
  const [more, setMore] = useState(false);
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(company ? { ...company } : { status: "prospect", owner_id: me.user.id, country: "IT" }); setDups(null); setMore(edit); } }, [open, company, edit, me]);
  const set = (p) => { setF((x) => ({ ...x, ...p })); setDups(null); };

  const payload = () => {
    const keys = ["name", "vat_number", "tax_code", "status", "category", "source", "notes", "owner_id", "first_name", "last_name", ...EXTRA.map((e) => e[0])];
    const out = Object.fromEntries(keys.map((k) => [k, (f[k] ?? "") === "" ? null : f[k]]));
    out.is_private = !!f.is_private;
    if (f.is_private) { out.name = `${(f.last_name || "").trim()} ${(f.first_name || "").trim()}`.trim(); out.vat_number = null; }
    if (!edit) {
      out.status = out.status || "prospect";
      if (contact.first_name && contact.last_name) out.contact = Object.fromEntries(Object.entries(contact).map(([k, v]) => [k, v || null]));
    }
    return out;
  };

  const save = async (force = false) => {
    setBusy(true);
    try {
      if (!edit && !force) {
        const p = payload();
        const { data } = await api.post("/crm/companies/check-duplicates", { name: p.name, vat_number: p.vat_number, tax_code: f.tax_code || null });
        if (data.matches.length || data.hidden) { setDups(data); setBusy(false); return; }
      }
      if (edit) {
        await api.patch(`/crm/companies/${company.id}`, payload());
        toast.success("Cliente aggiornato");
        onOpenChange(false); onSaved(company.id);
      } else {
        const { data } = await api.post("/crm/companies", { ...payload(), force });
        toast.success("Cliente creato");
        onOpenChange(false); onSaved(data.id);
      }
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="company-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{edit ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
          <DialogDescription>Obbligatori solo ragione sociale (o nome e cognome per un privato). Gli altri dati puoi aggiungerli quando servono.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <Section title="Dati principali">
            <label className="flex items-center gap-2 text-sm text-ink" data-testid="company-field-is_private-label">
              <Checkbox checked={!!f.is_private} onCheckedChange={(v) => set({ is_private: !!v })} data-testid="company-field-is_private" />
              Privato (persona fisica)<span className="text-xs text-slate-500">· fattura con codice fiscale, senza P.IVA</span></label>
            {f.is_private
              ? <div className="grid grid-cols-2 gap-3"><F label="Nome" k="first_name" f={f} set={set} req maxLength={80} /><F label="Cognome" k="last_name" f={f} set={set} req maxLength={80} /></div>
              : <F label="Ragione sociale" k="name" f={f} set={set} req maxLength={200} />}
            {f.is_private
              ? <F label="Codice fiscale" k="tax_code" f={f} set={set} maxLength={16} placeholder="16 caratteri, serve per fatturare" />
              : <div className="grid grid-cols-2 gap-3"><F label="Partita IVA" k="vat_number" f={f} set={set} maxLength={20} /><F label="Codice fiscale" k="tax_code" f={f} set={set} maxLength={20} /></div>}
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Stato" value={f.status} onChange={(v) => set({ status: v })} options={lk.company_status} testId="company-field-status" />
              <SelectField label="Categoria" value={f.category} onChange={(v) => set({ category: v })} options={lk.company_category} allowNone testId="company-field-category" />
            </div>
          </Section>
          {!edit && !f.is_private && can("contacts:create") && (
            <Section title="Contatto (facoltativo)">
              <div className="grid grid-cols-2 gap-3">
                {[["first_name", "Nome"], ["last_name", "Cognome"], ["job_title", "Ruolo"], ["email", "Email"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label><Input maxLength={k === "email" ? 254 : 80} value={contact[k]} onChange={(e) => setContact((c) => ({ ...c, [k]: e.target.value }))} data-testid={`company-contact-${k}`} /></div>
                ))}
              </div>
            </Section>
          )}
          <Section title="Responsabile">
            <SelectField value={f.owner_id} onChange={(v) => set({ owner_id: v })} options={can("companies:assign") ? users : users.filter((u) => u.id === (f.owner_id || me.user.id))} testId="company-field-owner" />
          </Section>
          <button type="button" onClick={() => setMore(!more)} className="flex items-center gap-1 text-sm font-semibold text-brand-700" data-testid="company-more-toggle">
            Informazioni aggiuntive<ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} /></button>
          {more && (
            <Section title="Informazioni aggiuntive">
              <div className="grid grid-cols-2 gap-3">{EXTRA.filter(([k]) => !(f.is_private && ["trade_name", "sdi_code"].includes(k))).map(([k, l]) => <F key={k} label={l} k={k} f={f} set={set} maxLength={k === "province" ? 4 : 200} />)}</div>
              <SelectField label="Origine" value={f.source} onChange={(v) => set({ source: v })} options={lk.lead_source} allowNone testId="company-field-source" />
              <div className="space-y-1.5"><Label className="text-xs">Note</Label><Textarea rows={3} maxLength={4000} value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="company-field-notes" /></div>
            </Section>
          )}
          {dups && (
            <div className="rounded-control border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="company-duplicate-warning">
              <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />Potrebbe esistere già un'azienda simile.</p>
              <ul className="mt-2 space-y-1">{dups.matches.map((m) => <li key={m.id}><Link to={`/clienti/${m.id}`} onClick={() => onOpenChange(false)} className="font-medium text-brand-700 underline" data-testid={`duplicate-open-${m.id}`}>{m.name}</Link> {m.vat_number && <span className="text-xs">· P.IVA {m.vat_number}</span>}</li>)}</ul>
              {dups.hidden > 0 && <p className="mt-1 text-xs">{dups.hidden} record simili non sono visibili con i tuoi permessi.</p>}
              {dups.exact && <p className="mt-1 text-xs font-semibold">Partita IVA o Codice Fiscale già presenti: non è possibile creare un duplicato.</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="company-form-cancel">Annulla</Button>
          {dups && !dups.exact && <Button variant="outline" onClick={() => save(true)} disabled={busy} data-testid="company-form-force">Procedi comunque</Button>}
          {!dups && <Button onClick={() => save(false)} disabled={busy || !(f.is_private ? (f.first_name || "").trim() && (f.last_name || "").trim() : (f.name || "").trim())} data-testid="company-form-save">{edit ? "Salva modifiche" : "Crea cliente"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
